import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import AdminImageUploader from "../../components/AdminImageUploader";

const empty = { title: "", summary: "", content: "", image_url: "", date: "" };

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = () => api.get("/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({ title: n.title, summary: n.summary, content: n.content, image_url: n.image_url, date: n.date?.slice(0, 10) || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.date) payload.date = new Date(form.date).toISOString();
      if (editing) {
        await api.put(`/news/${editing.id}`, payload);
        toast.success("News updated");
      } else {
        await api.post("/news", payload);
        toast.success("News created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try {
      await api.delete(`/news/${n.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-news-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Newsroom</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">News &amp; Updates</h1>
        </div>
        <Button data-testid="new-news-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Article
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Title</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold">{n.title}</td>
                <td className="p-3 text-slate-600 font-mono text-xs">{new Date(n.date).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => openEdit(n)} data-testid={`edit-news-${n.id}`} className="p-2 hover:bg-slate-100"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => del(n)} data-testid={`delete-news-${n.id}`} className="p-2 hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No news yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit News" : "New News"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Title *</Label>
              <Input data-testid="news-form-title" required value={form.title} onChange={setField("title")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Summary</Label>
              <Input value={form.summary} onChange={setField("summary")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Content *</Label>
              <Textarea required rows={6} value={form.content} onChange={setField("content")} className="rounded-none" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <AdminImageUploader
                label="Featured Image"
                value={form.image_url}
                onChange={(v) => setForm({ ...form, image_url: v })}
                testId="news-image"
                onBusy={setImgBusy}
              />
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Date</Label>
                <Input type="date" value={form.date} onChange={setField("date")} className="rounded-none" />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {imgBusy && (
                <span className="text-xs text-amber-600 font-bold uppercase tracking-wider mr-auto inline-flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Uploading image…
                </span>
              )}
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button
                data-testid="news-form-submit"
                type="submit"
                disabled={busy || imgBusy}
                title={imgBusy ? "Wait for image upload to finish" : ""}
                className="rounded-none btn-amber font-semibold disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
